import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import { BehaviorSubject, firstValueFrom, take } from 'rxjs';

import { TokenStorageService } from './token-storage.service';

const PUSH_ENDPOINTS = {
  publicKey: '/api/push/vapid-public-key',
  subscriptions: '/api/push/subscriptions',
} as const;

const PUSH_OFFER_DISMISSED_KEY =
  'mindyourtax.push-notifications.offer-dismissed';

export type PushNotificationStatus =
  | 'unsupported'
  | 'disabled-by-server'
  | 'permission-denied'
  | 'off'
  | 'on';

export interface PushNotificationState {
  status: PushNotificationStatus;
  message: string;
}

interface ApiResponse<T> {
  status: string;
  message: string | null;
  data: T;
}

interface VapidPublicKeyData {
  publicKey: string;
  enabled: boolean;
}

interface SubscriptionResponseData {
  subscriptionId: number;
  active: boolean;
}

interface UnsubscribeResponseData {
  deactivated: boolean;
}

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private readonly stateSubject = new BehaviorSubject<PushNotificationState>({
    status: 'unsupported',
    message: 'Checking notification support…',
  });

  readonly state$ = this.stateSubject.asObservable();

  private vapidConfig: VapidPublicKeyData | null = null;
  private initializedUserId: number | null = null;
  private initialization: Promise<void> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly swPush: SwPush,
    private readonly tokenStorageService: TokenStorageService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {
    if (this.isBrowser && this.swPush.isEnabled) {
      this.swPush.notificationClicks.subscribe(({ notification }) => {
        const route = notification.data?.route;
        if (
          typeof route === 'string' &&
          route.startsWith('/') &&
          !route.startsWith('//')
        ) {
          void this.router.navigateByUrl(route);
        }
      });
    }
  }

  get currentState(): PushNotificationState {
    return this.stateSubject.value;
  }

  get notificationPermission(): NotificationPermission | 'unsupported' {
    if (!this.isBrowser || !('Notification' in window)) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  isCurrentUserEligibleForPushSurface(): boolean {
    const user = this.tokenStorageService.userProfile.value;
    const currentRole =
      user?.role ?? this.tokenStorageService.currentUserRole;
    return !!user?.userId && currentRole !== 'CUSTOMER';
  }

  async initializeForCurrentUser(): Promise<void> {
    if (!this.isCurrentUserEligibleForPushSurface()) {
      return;
    }

    const userId = this.tokenStorageService.currentUserId;
    if (userId === null) {
      return;
    }

    if (this.initializedUserId === userId && this.initialization) {
      return this.initialization;
    }

    this.initializedUserId = userId;
    this.initialization = this.initialize();
    return this.initialization;
  }

  async enable(): Promise<void> {
    if (!this.isCurrentUserEligibleForPushSurface()) {
      return;
    }

    if (!this.vapidConfig) {
      this.setState(
        'disabled-by-server',
        'System notifications are not ready yet. Please retry.',
      );
      return;
    }
    const capabilityIssue = this.getCapabilityIssue();
    if (capabilityIssue) {
      this.setState('unsupported', capabilityIssue);
      return;
    }

    if (!this.vapidConfig?.enabled) {
      this.setState(
        'disabled-by-server',
        'System notifications are unavailable right now.',
      );
      return;
    }

    try {
      const subscription = await this.swPush.requestSubscription({
        serverPublicKey: this.vapidConfig.publicKey,
      });
      const json = subscription.toJSON();
      const p256dh = json.keys?.['p256dh'];
      const auth = json.keys?.['auth'];

      if (!json.endpoint || !p256dh || !auth) {
        throw new Error('Browser returned an incomplete push subscription');
      }

      const response = await firstValueFrom(
        this.http.post<ApiResponse<SubscriptionResponseData>>(
          PUSH_ENDPOINTS.subscriptions,
          {
            endpoint: json.endpoint,
            p256dh,
            auth,
            userAgent: navigator.userAgent.slice(0, 300),
          },
        ),
      );

      if (!response.data.active) {
        throw new Error('Server did not activate the push subscription');
      }

      this.setState('on', 'System notifications are on.');
    } catch (error) {
      if (this.notificationPermission === 'denied') {
        this.setState(
          'permission-denied',
          'Notifications are blocked in your browser settings.',
        );
        return;
      }

      await this.unsubscribeLocallyBestEffort();
      this.setState('off', 'System notifications are off.');
      throw error;
    }
  }

  async disable(): Promise<void> {
    const subscription = await this.getSubscriptionBestEffort();
    if (subscription) {
      await this.deactivateEndpointBestEffort(subscription.endpoint);
      await this.unsubscribeLocallyBestEffort();
    }
    this.setState('off', 'System notifications are off.');
  }

  async cleanupBeforeLogout(): Promise<void> {
    if (!this.isCurrentUserEligibleForPushSurface()) {
      this.resetForSignedOutUser();
      return;
    }

    const subscription = await this.getSubscriptionBestEffort();
    if (subscription) {
      await this.deactivateEndpointBestEffort(subscription.endpoint);
      await this.unsubscribeLocallyBestEffort();
    }
    this.resetForSignedOutUser();
  }

  shouldOfferPermission(): boolean {
    return (
      this.currentState.status === 'off' &&
      this.notificationPermission === 'default' &&
      !this.wasOfferDismissed()
    );
  }

  markOfferDismissed(): void {
    if (!this.isBrowser) {
      return;
    }
    try {
      localStorage.setItem(PUSH_OFFER_DISMISSED_KEY, 'true');
    } catch {
      // Storage can be unavailable in restricted browser modes.
    }
  }

  resetForSignedOutUser(): void {
    this.vapidConfig = null;
    this.initializedUserId = null;
    this.initialization = null;
    this.setState('unsupported', 'Checking notification support…');
  }

  private async initialize(): Promise<void> {
    const capabilityIssue = this.getCapabilityIssue();
    if (capabilityIssue) {
      this.setState('unsupported', capabilityIssue);
      return;
    }

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<VapidPublicKeyData>>(
          PUSH_ENDPOINTS.publicKey,
        ),
      );

      this.vapidConfig = {
        enabled: response.data.enabled,
        publicKey: response.data.publicKey,
      };
      if (!response.data.enabled) {
        this.setState(
          'disabled-by-server',
          'System notifications are unavailable right now.',
        );
        return;
      }

      if (this.notificationPermission === 'denied') {
        this.setState(
          'permission-denied',
          'Notifications are blocked in your browser settings.',
        );
        return;
      }

      const subscription = await firstValueFrom(
        this.swPush.subscription.pipe(take(1)),
      );
      this.setState(
        subscription ? 'on' : 'off',
        subscription
          ? 'System notifications are on.'
          : 'System notifications are off.',
      );
    } catch (error) {
      console.error(
        '[PushNotificationService] failed to load push eligibility',
        error,
      );
      this.vapidConfig = null;
      this.setState(
        'disabled-by-server',
        'System notifications are unavailable right now.',
      );
    }
  }

  private getCapabilityIssue(): string | null {
    if (!this.isBrowser) {
      return 'System notifications require a supported browser.';
    }

    if (this.isIosDevice() && !this.isStandaloneDisplay()) {
      return 'On iPhone or iPad, add MindYourTax to the Home Screen first.';
    }

    if (
      !('serviceWorker' in navigator) ||
      !('PushManager' in window) ||
      !('Notification' in window) ||
      !this.swPush.isEnabled
    ) {
      return 'System notifications are not supported in this browser.';
    }

    return null;
  }

  private isIosDevice(): boolean {
    const navigatorWithPlatform = navigator as Navigator & {
      platform?: string;
    };
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigatorWithPlatform.platform === 'MacIntel' &&
        navigator.maxTouchPoints > 1)
    );
  }

  private isStandaloneDisplay(): boolean {
    const navigatorWithStandalone = navigator as Navigator & {
      standalone?: boolean;
    };
    return (
      navigatorWithStandalone.standalone === true ||
      window.matchMedia?.('(display-mode: standalone)').matches === true
    );
  }

  private async getSubscriptionBestEffort(): Promise<PushSubscription | null> {
    if (!this.isBrowser || !this.swPush.isEnabled) {
      return null;
    }
    try {
      return await firstValueFrom(this.swPush.subscription.pipe(take(1)));
    } catch (error) {
      console.error(
        '[PushNotificationService] failed to read local subscription',
        error,
      );
      return null;
    }
  }

  private async deactivateEndpointBestEffort(endpoint: string): Promise<void> {
    try {
      await firstValueFrom(
        this.http.delete<ApiResponse<UnsubscribeResponseData>>(
          PUSH_ENDPOINTS.subscriptions,
          { body: { endpoint } },
        ),
      );
    } catch (error) {
      console.error(
        '[PushNotificationService] failed to deactivate server subscription',
        error,
      );
    }
  }

  private async unsubscribeLocallyBestEffort(): Promise<void> {
    if (!this.isBrowser || !this.swPush.isEnabled) {
      return;
    }
    try {
      await this.swPush.unsubscribe();
    } catch (error) {
      console.error(
        '[PushNotificationService] failed to remove local subscription',
        error,
      );
    }
  }

  private wasOfferDismissed(): boolean {
    if (!this.isBrowser) {
      return true;
    }
    try {
      return localStorage.getItem(PUSH_OFFER_DISMISSED_KEY) === 'true';
    } catch {
      return true;
    }
  }

  private setState(status: PushNotificationStatus, message: string): void {
    this.stateSubject.next({ status, message });
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}
